import * as THREE from "three";
import { Damped } from "./easing";

if (import.meta.hot) import.meta.hot.invalidate();

/**
 * Flat-map camera.
 *
 * Orthographic projection, positioned south of origin and elevated above
 * the map plane. Slight tilt so arcs visibly lift off the ground rather
 * than appearing as flat lines.
 *
 * Static framing — the whole world is always visible. A subtle cosine
 * parallax on the view height and target Y gives the map a gentle "breath"
 * so it never reads as a screenshot.
 */
export class GlobeCamera {
  readonly camera: THREE.OrthographicCamera;
  readonly root: THREE.Group;

  /** Vertical world-units visible. Width derived from aspect ratio.
   *  2.6 crops Antarctica and far Pacific; centers Africa/EU/ME/India/NY. */
  private static readonly VIEW_HEIGHT = 2.6;

  /** Subtle parallax target offsets (world units). */
  private parallaxY = new Damped(0, 0, 4000);
  private parallaxZoom = new Damped(0, 0, 5000);

  /** Corridor zoom — dolly into the most active corridor. */
  private zoomLevel = new Damped(0, 0, 1200);
  private panOffsetX = new Damped(0, 0, 1000);
  private panOffsetY = new Damped(0, 0, 1000);

  private phase = Math.random() * Math.PI * 2;
  private slowFactor = 1;

  constructor(aspect: number) {
    this.camera = new THREE.OrthographicCamera(
      -GlobeCamera.VIEW_HEIGHT * aspect * 0.5,
      GlobeCamera.VIEW_HEIGHT * aspect * 0.5,
      GlobeCamera.VIEW_HEIGHT * 0.5,
      -GlobeCamera.VIEW_HEIGHT * 0.5,
      0.01,
      100,
    );
    // Elevated and tilted south so arcs lift "upward" on screen.
    // lookAt shifted north so Europe composes above Africa rather than
    // pushing Africa to the center and cropping the EU corridors.
    this.camera.position.set(0, -0.45, 4);
    this.camera.up.set(0, 1, 0);
    this.camera.lookAt(0, 0.28, 0);

    this.root = new THREE.Group();
    this.root.name = "mapRoot";
  }

  setAspect(aspect: number) {
    const h =
      (GlobeCamera.VIEW_HEIGHT + this.parallaxZoom.value) *
      (1 - this.zoomLevel.value * 0.45);
    this.camera.left = -h * aspect * 0.5;
    this.camera.right = h * aspect * 0.5;
    this.camera.top = h * 0.5;
    this.camera.bottom = -h * 0.5;
    this.camera.updateProjectionMatrix();
  }

  /** Bias is a no-op on the flat map — the whole world is always in frame. */
  biasToLongitude(_lng: number) {}

  /**
   * Smoothly dolly into the midpoint of a corridor defined by two
   * world-plane XY positions. ~1.4x magnification over 1.2s.
   */
  zoomToCorridor(x1: number, y1: number, x2: number, y2: number) {
    const mx = (x1 + x2) / 2;
    const my = (y1 + y2) / 2;
    this.panOffsetX.target = mx;
    this.panOffsetY.target = my;
    this.zoomLevel.target = 1;
  }

  /** Ease back to the global view. */
  resetZoom() {
    this.panOffsetX.target = 0;
    this.panOffsetY.target = 0;
    this.zoomLevel.target = 0;
  }

  setSlowMode(slow: boolean) {
    this.slowFactor = slow ? 0.5 : 1;
  }

  update(dtMs: number) {
    this.phase += (dtMs / 1000) * 0.12 * this.slowFactor;

    // Slow, low-amplitude breath — imperceptible if you're not looking for it.
    this.parallaxY.target = Math.sin(this.phase) * 0.06;
    this.parallaxZoom.target = Math.cos(this.phase * 0.72) * 0.08;

    this.parallaxY.update(dtMs);
    this.parallaxZoom.update(dtMs);
    this.zoomLevel.update(dtMs);
    this.panOffsetX.update(dtMs);
    this.panOffsetY.update(dtMs);

    // Update camera framing.
    // Zoom shrinks VIEW_HEIGHT by up to 45% (1.4x magnification).
    const aspect =
      (this.camera.right - this.camera.left) / (this.camera.top - this.camera.bottom);
    const h =
      (GlobeCamera.VIEW_HEIGHT + this.parallaxZoom.value) *
      (1 - this.zoomLevel.value * 0.45);
    this.camera.left = -h * aspect * 0.5;
    this.camera.right = h * aspect * 0.5;
    this.camera.top = h * 0.5;
    this.camera.bottom = -h * 0.5;
    this.camera.lookAt(
      this.panOffsetX.value,
      0.28 + this.parallaxY.value + this.panOffsetY.value,
      0,
    );
    this.camera.updateProjectionMatrix();
  }
}
